import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import FarmCard from "@/components/FarmCard";
import FarmMap from "@/components/FarmMap";
import ActivityFilters from "@/components/ActivityFilters";
import { Search, Compass, X, MapIcon, List, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Directory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [farms, setFarms] = useState([]);
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(searchParams.get("activity") ? [searchParams.get("activity")] : []);
  const [userLoc, setUserLoc] = useState(null);
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [viewMobile, setViewMobile] = useState("list");
  const [favorites, setFavorites] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.get("/activities").then((r) => setActivities(r.data)); }, []);

  useEffect(() => {
    if (searchParams.get("near") === "me" && !userLoc) {
      handleNearMe();
    }
    // eslint-disable-next-line
  }, []);

  const fetchFarms = async () => {
    const params = {};
    if (search) params.search = search;
    if (selected.length === 1) params.activity = selected[0];
    if (userLoc) { params.lat = userLoc[0]; params.lng = userLoc[1]; }
    const { data } = await api.get("/companies", { params });
    let result = data;
    if (selected.length > 1) {
      result = result.filter((f) => selected.every((s) => f.activities.includes(s)));
    }
    setFarms(result);
  };

  useEffect(() => { fetchFarms(); /* eslint-disable-next-line */ }, [search, selected, userLoc]);

  useEffect(() => {
    if (user) api.get("/favorites").then((r) => setFavorites(r.data.map((f) => f.id))).catch(() => {});
  }, [user]);

  const handleNearMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non supportata dal browser"); return; }
    setLoadingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc([pos.coords.latitude, pos.coords.longitude]);
        toast.success("Posizione individuata! Fattorie ordinate per vicinanza.");
        setLoadingLoc(false);
      },
      () => { toast.error("Impossibile ottenere la posizione. Controlla i permessi del browser."); setLoadingLoc(false); }
    );
  };

  const toggleActivity = (slug) => {
    setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("activity");
    setSearchParams(newParams);
  };

  const toggleFavorite = async (farm) => {
    if (!user) { toast.info("Accedi per salvare i preferiti"); navigate("/login"); return; }
    const isFav = favorites.includes(farm.id);
    try {
      if (isFav) {
        await api.delete(`/favorites/${farm.id}`);
        setFavorites((f) => f.filter((x) => x !== farm.id));
      } else {
        await api.post(`/favorites/${farm.id}`);
        setFavorites((f) => [...f, farm.id]);
        toast.success("Aggiunto ai preferiti");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearFilters = () => {
    setSelected([]); setSearch(""); setUserLoc(null);
    setSearchParams({});
  };

  const hasFilters = selected.length > 0 || search || userLoc;

  return (
    <div className="bg-[#FBF9F6] min-h-[calc(100vh-80px)]">
      {/* Top bar */}
      <div className="border-b border-[#E5DCD0] bg-white sticky top-20 z-30">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-5 flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#7A7369]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, città o descrizione…"
                className="w-full h-14 rounded-full border-2 border-[#E5DCD0] bg-white pl-14 pr-4 text-base font-medium text-[#2C312E] placeholder:text-[#7A7369] focus:outline-none focus:ring-2 focus:ring-[#4A6B53] focus:border-transparent transition-all"
                data-testid="directory-search"
              />
            </div>
            <button
              onClick={handleNearMe}
              disabled={loadingLoc}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C8795A] text-white h-14 px-7 text-base font-bold hover:bg-[#B3684B] hover:-translate-y-0.5 transition-all disabled:opacity-60"
              data-testid="near-me-btn"
            >
              {loadingLoc ? <Loader2 size={18} className="animate-spin" /> : <Compass size={18} />}
              {userLoc ? "Posizione attiva" : "Vicino a me"}
            </button>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E5DCD0] h-14 px-6 text-sm font-bold hover:border-[#C8795A] hover:text-[#C8795A]"
                data-testid="clear-filters"
              >
                <X size={16} /> Reset
              </button>
            )}
          </div>
          <ActivityFilters activities={activities} selected={selected} onToggle={toggleActivity} />
        </div>
      </div>

      {/* mobile view toggle */}
      <div className="lg:hidden flex justify-center pt-4">
        <div className="inline-flex rounded-full bg-white border-2 border-[#E5DCD0] p-1">
          <button onClick={() => setViewMobile("list")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${viewMobile === "list" ? "bg-[#4A6B53] text-white" : "text-[#58625B]"}`} data-testid="mobile-list-view"><List size={14}/> Lista</button>
          <button onClick={() => setViewMobile("map")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${viewMobile === "map" ? "bg-[#4A6B53] text-white" : "text-[#58625B]"}`} data-testid="mobile-map-view"><MapIcon size={14}/> Mappa</button>
        </div>
      </div>

      {/* Split view */}
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-6 grid lg:grid-cols-5 gap-6 min-h-[700px]">
        {/* List */}
        <div className={`lg:col-span-2 ${viewMobile === "map" ? "hidden lg:block" : ""}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-extrabold text-xl text-[#2C312E]">
              {farms.length} {farms.length === 1 ? "fattoria trovata" : "fattorie trovate"}
            </h2>
            {userLoc && <span className="text-xs font-bold text-[#C8795A] uppercase tracking-wider">ordinate per distanza</span>}
          </div>
          <div className="agri-scroll grid grid-cols-1 gap-5 lg:max-h-[calc(100vh-260px)] lg:overflow-y-auto pr-2">
            {farms.length === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center">
                <p className="font-display font-bold text-[#2C312E] mb-2">Nessuna fattoria corrisponde ai filtri</p>
                <p className="text-sm text-[#58625B]">Prova a modificare la ricerca o il raggio di distanza.</p>
              </div>
            ) : farms.map((f) => (
              <FarmCard
                key={f.id}
                farm={f}
                onClick={() => setActiveId(f.id)}
                isFavorite={favorites.includes(f.id)}
                onFavorite={toggleFavorite}
              />
            ))}
          </div>
        </div>

        {/* Map */}
        <div className={`lg:col-span-3 ${viewMobile === "list" ? "hidden lg:block" : ""}`}>
          <div className="lg:sticky lg:top-[210px]">
            <FarmMap
              farms={farms}
              userLocation={userLoc}
              activeId={activeId}
              onMarkerClick={(f) => navigate(`/fattorie/${f.id}`)}
              height={typeof window !== "undefined" && window.innerWidth < 1024 ? 500 : 700}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
